import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatDate } from "@/lib/catalog";
import { Skeleton, EmptyState } from "@/components/Ui";
import { ArrowLeft } from "lucide-react";

function mdToHtml(md) {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) =>
    esc(s)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  const blocks = md.split(/\n{2,}/);
  return blocks
    .map((block) => {
      const lines = block.split("\n").filter(Boolean);
      if (/^###\s/.test(lines[0])) return `<h3>${inline(lines[0].replace(/^###\s*/, ""))}</h3>`;
      if (/^##\s/.test(lines[0])) return `<h2>${inline(lines[0].replace(/^##\s*/, ""))}</h2>`;
      if (/^#\s/.test(lines[0])) return `<h1>${inline(lines[0].replace(/^#\s*/, ""))}</h1>`;
      if (lines.every((l) => /^[-*]\s/.test(l)))
        return `<ul>${lines.map((l) => `<li>${inline(l.replace(/^[-*]\s*/, ""))}</li>`).join("")}</ul>`;
      if (lines.every((l) => /^\d+[.)]\s/.test(l)))
        return `<ol>${lines.map((l) => `<li>${inline(l.replace(/^\d+[.)]\s*/, ""))}</li>`).join("")}</ol>`;
      return `<p>${lines.map(inline).join("<br/>")}</p>`;
    })
    .join("");
}

export default function LegalPage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [state, setState] = useState({ page: null, loading: true, notFound: false });

  useEffect(() => {
    setState({ page: null, loading: true, notFound: false });
    apiGet(`/pages/${slug}`)
      .then((page) => setState({ page, loading: false, notFound: false }))
      .catch((err) => setState({ page: null, loading: false, notFound: err.status === 404 }));
  }, [slug]);

  const { page, loading, notFound } = state;

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6" data-testid="legal-loading">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="mt-4 h-4 w-40" />
        <Skeleton className="mt-8 h-4 w-full" />
        <Skeleton className="mt-2 h-4 w-full" />
        <Skeleton className="mt-2 h-4 w-5/6" />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 sm:px-6" data-testid="legal-notfound">
        <EmptyState message={t("legal.notFound")} testid="legal-empty" />
        <div className="mt-6 text-center">
          <Link to="/" className="btn-outline" data-testid="legal-back-home">
            <ArrowLeft className="h-4 w-4" /> {t("form.backHome")}
          </Link>
        </div>
      </div>
    );
  }

  const html = page.format === "html" ? page.body : mdToHtml(page.body || "");

  return (
    <div className="bg-grid" data-testid="legal-page">
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <h1 className="font-display text-4xl font-bold text-marine sm:text-5xl" data-testid="legal-title">
          {page.title}
        </h1>
        {page.updated_at && (
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-gold" data-testid="legal-updated">
            {t("legal.updated")} {formatDate(page.updated_at, lang)}
          </p>
        )}
        <div className="prose-mg mt-8" data-testid="legal-body" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}
